<template>
  <div class="bg-white rounded-lg pb-6 px-6">
    <div class="flex flex-col sm:flex-row gap-4">
      <div class="flex-1">
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            :value="searchQuery"
            @input="$emit('update:searchQuery', ($event.target as HTMLInputElement).value)"
            type="text"
            placeholder="Search by tutor name or subject..."
            class="block w-full pl-10 pr-3 py-2 border border-gray-500 rounded-md text-gray-500 leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
      
      <!-- Status Filter Dropdown -->
      <div class="sm:w-48 relative">
        <button
          @click="toggleStatusDropdown"
          class="block w-full px-3 py-2 border border-gray-500 rounded-md text-gray-500 leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 cursor-pointer pr-10 text-left"
        >
          {{ getStatusDisplayText(statusFilter) }}
        </button>
        <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        
        <!-- Status Dropdown Menu -->
        <div
          v-if="isStatusDropdownOpen"
          class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
        >
          <div
            v-for="option in statusOptions"
            :key="option.value"
            @click="selectStatus(option.value)"
            class="px-3 py-1.5 cursor-pointer hover:bg-gray-100 first:rounded-t-md last:rounded-b-md text-sm"
            :class="{ 'bg-blue-50 text-blue-600': option.value === statusFilter, 'text-gray-700': option.value !== statusFilter }"
          >
            {{ option.label }}
          </div>
        </div>
      </div>
      
      <!-- Subject Filter Dropdown -->
      <div class="sm:w-64 relative">
        <button
          @click="toggleSubjectDropdown"
          class="block w-full px-3 py-2 border border-gray-500 rounded-md text-gray-500 leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 cursor-pointer pr-10 text-left truncate"
          :title="getSubjectDisplayText(subjectFilter)"
        >
          {{ getSubjectDisplayText(subjectFilter) }}
        </button>
        <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        
        <!-- Subject Dropdown Menu -->
        <div
          v-if="isSubjectDropdownOpen"


          class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
        >
          <div
            v-for="option in subjectOptions"
            :key="option.value"
            @click="selectSubject(option.value)"
            class="px-3 py-1.5 cursor-pointer hover:bg-gray-100 first:rounded-t-md last:rounded-b-md text-sm truncate"
            :class="{ 'bg-blue-50 text-blue-600': option.value === subjectFilter, 'text-gray-700': option.value !== subjectFilter }"
            :title="option.label"
          >
            {{ option.label }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'

interface Props {
  searchQuery: string
  statusFilter: string
  subjectFilter: string
  filterContext?: 'active' | 'history' // New prop to determine which filters to show
}

const props = withDefaults(defineProps<Props>(), {
  filterContext: 'active'
})

const emit = defineEmits<{
  'update:searchQuery': [value: string]
  'update:statusFilter': [value: string]
  'update:subjectFilter': [value: string]
}>()

// Dropdown state
const isStatusDropdownOpen = ref(false)
const isSubjectDropdownOpen = ref(false)

// Options data - now computed based on context
const statusOptions = computed(() => {
  if (props.filterContext === 'history') {
    return [
      { value: 'all', label: 'All Status' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' },
      { value: 'declined', label: 'Declined' }
    ]
  } else {
    return [
      { value: 'all', label: 'All Status' },
      { value: 'confirmed', label: 'Confirmed' },
      { value: 'pending', label: 'Pending' }
    ]
  }
})

const subjectOptions = [
  { value: 'all', label: 'All Subjects' },
  { value: 'oral-communication', label: 'Oral Communication' },
  { value: 'reading-and-writing', label: 'Reading and Writing' },
  { value: '21st-century-literature', label: '21st Century Literature' },
  { value: 'komunikasyon-at-pananaliksik', label: 'Komunikasyon at Pananaliksik' },
  { value: 'earth-and-life-science', label: 'Earth and Life Science' },
  { value: 'physical-science', label: 'Physical Science' },
  { value: 'pagbasa-at-pagsusuri', label: 'Pagbasa at Pagsusuri' },
  { value: 'general-mathematics-statistics', label: 'General Mathematics & Statistics' }
]

// Helper functions
const getStatusDisplayText = (value: string) => {
  const option = statusOptions.value.find(opt => opt.value === value)
  return option ? option.label : 'All Status'
}

const getSubjectDisplayText = (value: string) => {
  const option = subjectOptions.find(opt => opt.value === value)
  return option ? option.label : 'All Subjects'
}

// Dropdown toggle functions
const toggleStatusDropdown = () => {
  isStatusDropdownOpen.value = !isStatusDropdownOpen.value
  isSubjectDropdownOpen.value = false // Close other dropdown
}

const toggleSubjectDropdown = () => {
  isSubjectDropdownOpen.value = !isSubjectDropdownOpen.value
  isStatusDropdownOpen.value = false // Close other dropdown
}

// Selection functions
const selectStatus = (value: string) => {
  emit('update:statusFilter', value)
  isStatusDropdownOpen.value = false
}

const selectSubject = (value: string) => {
  emit('update:subjectFilter', value)
  isSubjectDropdownOpen.value = false
}

// Close dropdowns when clicking outside
const closeDropdowns = () => {
  isStatusDropdownOpen.value = false
  isSubjectDropdownOpen.value = false
}

// Add click outside listener
onMounted(() => {
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    if (!target.closest('.sm\\:w-48') && !target.closest('.sm\\:w-64')) {
      closeDropdowns()
    }
  })
})

onUnmounted(() => {
  document.removeEventListener('click', closeDropdowns)
})
</script>
